import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { ctaButtonCss } from '../utils/ctaHover';
import { CtaArrow } from './shared/CtaArrow';

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface FaqCategoryGroup {
  /** Empty for FAQs without a category. */
  name: string;
  items: { faq: Faq; index: number }[];
}

function groupByCategory(faqs: Faq[]): FaqCategoryGroup[] {
  const groups = new Map<string, FaqCategoryGroup>();
  faqs.forEach((faq, index) => {
    const name = stripHtml(faq.category);
    let group = groups.get(name);
    if (!group) {
      group = { name, items: [] };
      groups.set(name, group);
    }
    group.items.push({ faq, index });
  });
  return Array.from(groups.values());
}

const contactLinkDef: SlotGroupDefinition = {
  label: 'Contact CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const FLOOR_PLANS_HREF = '/floor-plans';
const OCCUPANCY_QUALIFICATIONS_HREF =
  'https://www.pricebrotherskc.com/ResidentialRentalQualifications/';

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return String(html).replace(/<[^>]*>/g, '').trim();
}

function hasHref(html: string, href: string): boolean {
  return html.includes(`href="${href}"`) || html.includes(`href='${href}'`);
}

/** Inject company FAQ cross-links when the property answer text lacks them. */
function enhanceFaqAnswer(html: string | null | undefined): string {
  if (!html) return '';
  let result = html;

  // "Floor Plans" tab → floor plans page
  if (!hasHref(result, FLOOR_PLANS_HREF)) {
    result = result.replace(
      /("Floor Plans"|Floor Plans)(?=\s+tab)/gi,
      `<a href="${FLOOR_PLANS_HREF}">$1</a>`,
    );
  }

  // "here" after occupancy qualifications → corporate rental criteria
  if (!hasHref(result, OCCUPANCY_QUALIFICATIONS_HREF)) {
    result = result.replace(
      /(occupancy qualifications\s+)here\b/gi,
      `$1<a href="${OCCUPANCY_QUALIFICATIONS_HREF}" target="_blank" rel="noopener noreferrer">here</a>`,
    );
  }

  return result;
}

interface FaqItemProps {
  faq: Faq;
  index: number;
  open: boolean;
  onToggle: () => void;
}

function FaqItem({ faq, index, open, onToggle }: FaqItemProps) {
  const question = stripHtml(faq.question) || 'Question';
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;
  const answerHtml = enhanceFaqAnswer(faq.answer);

  return (
    <div className="border-b border-[var(--vibe-color-border)]">
      <h3 className="m-0">
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className="w-full flex items-center justify-between gap-4 py-5 text-left font-heading text-base sm:text-lg font-normal text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
        >
          <span className="tracking-wide">{question}</span>
          <span
            aria-hidden="true"
            className={`shrink-0 text-[var(--vibe-color-primary)] transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        className="pb-5 pr-6 font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:text-[var(--vibe-color-primary)] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1"
        dangerouslySetInnerHTML={{ __html: answerHtml }}
      />
    </div>
  );
}

export function FaqList() {
  const emptyText = useContentToken('faqlist.empty_text');
  const uncategorizedLabel = useContentToken('faqlist.uncategorized_label');
  const stillHaveQuestionsHeading = useContentToken('faqlist.still_have_questions_heading');
  const contactLink = useSlotGroup('faqlist.contact_link', contactLinkDef);

  const faqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
    defaultLimit: 40,
  });

  const [openKey, setOpenKey] = useState<number | null>(null);
  const groups = groupByCategory(faqs);
  const headingClass =
    'font-heading text-xl sm:text-2xl font-normal tracking-wide text-[var(--vibe-color-primary)] mb-4';

  return (
    <section className="bg-[var(--vibe-color-background)] py-12 md:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
          {faqs.length === 0 ? (
            <EditableToken
              path="faqlist.empty_text"
              as="p"
              className="text-center font-body text-base text-[var(--vibe-color-text-muted)] py-8"
            >
              {emptyText}
            </EditableToken>
          ) : (
            <div className="space-y-12">
              {groups.map((group) => (
                <div key={group.name}>
                  {group.name ? (
                    <h2 className={headingClass}>{group.name}</h2>
                  ) : (
                    <EditableToken path="faqlist.uncategorized_label" as="h2" className={headingClass}>
                      {uncategorizedLabel}
                    </EditableToken>
                  )}
                  <div className="border-t border-[var(--vibe-color-border)]">
                    {group.items.map(({ faq, index }) => (
                      <FaqItem
                        key={index}
                        faq={faq}
                        index={index}
                        open={openKey === index}
                        onToggle={() => setOpenKey((prev) => (prev === index ? null : index))}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </EditableCollectionBinding>

        <div className="mt-12 text-center">
          <EditableToken
            path="faqlist.still_have_questions_heading"
            as="h3"
            className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-3 tracking-wide"
          >
            {stillHaveQuestionsHeading}
          </EditableToken>
          <RichTextContent
            path="faqlist.still_have_questions_body"
            label="Still have questions body"
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 max-w-xl mx-auto"
          />

          <EditableSlotGroup
            groupPath="faqlist.contact_link"
            definition={contactLinkDef}
          >
            {contactLink.url && (
              <div className="mt-6">
                <a
                  href={contactLink.url}
                  target={contactLink._target || '_self'}
                  className="faq-contact-cta"
                >
                  <span>{contactLink.label || 'Contact Us'}</span>
                  <CtaArrow />
                </a>
              </div>
            )}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{ctaButtonCss('.faq-contact-cta')}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.empty_text': {
      label: 'Empty State Text',
      type: 'text',
      defaultValue: 'Frequently asked questions will be displayed here.',
    },
    'faqlist.uncategorized_label': {
      label: 'Uncategorized FAQs Heading',
      type: 'text',
      defaultValue: 'General',
    },
    'faqlist.still_have_questions_heading': {
      label: 'Still Have Questions Heading',
      type: 'text',
      defaultValue: 'Still have questions?',
    },
    'faqlist.still_have_questions_body': {
      label: 'Still Have Questions Body',
      type: 'richtext',
      defaultValue:
        '<p>Our leasing team is happy to help. Reach out and we&rsquo;ll get back to you as soon as we can.</p>',
    },
  },
  slotGroups: {
    'faqlist.contact_link': contactLinkDef,
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
      defaultLimit: 40,
      description: 'Questions and answers grouped under their category headings.',
    },
  },
};

(FaqList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqList;
